import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  TextField,
  Divider,
  Switch,
  FormControlLabel,
  Snackbar,
  Alert,
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  BusinessOutlined,
  SaveOutlined,
  AddCircleOutline,
  TuneOutlined,
  PhoneOutlined,
  EmailOutlined,
  LocationOnOutlined,
  StorefrontOutlined,
  GroupOutlined,
  PersonAddAlt1Outlined,
  KeyOutlined,
} from '@mui/icons-material';
import Layout from '../components/Layout';
import LoadingState from '../components/LoadingState';
import { companyService } from '../api/services';
import { useLanguage } from '../context/LanguageContext';
import { getApiErrorMessage } from '../utils/errorHandler';

const CompanyProfile = () => {
  const { t, isRtl, dir } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [savingCompany, setSavingCompany] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  const [savingBranch, setSavingBranch] = useState(false);
  const [company, setCompany] = useState({
    companyNameAr: 'فاست لاين للتوصيل',
    companyNameEn: 'FastLine Delivery',
    companyCode: 'FASTLINE-01',
    phone: '01000000000',
    email: 'info@fastline-delivery.com',
    currencyCode: 'EGP',
    timeZoneId: 'Africa/Cairo',
    logoUrl: '',
  });
  const [branches, setBranches] = useState([]);
  const [settings, setSettings] = useState({
    businessDayStartHour: 6,
    autoAcceptOrders: true,
    defaultDispatchTimeoutSeconds: 45,
  });

  // Company Users State
  const [companyUsers, setCompanyUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [addUserOpen, setAddUserOpen] = useState(false);
  const [savingUser, setSavingUser] = useState(false);
  const [newCompanyUser, setNewCompanyUser] = useState({
    userName: '',
    fullName: '',
    email: '',
    mobile: '',
    password: '',
    isActive: true,
    roleIds: [1],
  });

  // Reset Password State
  const [resetPwdOpen, setResetPwdOpen] = useState(false);
  const [selectedUserForPwd, setSelectedUserForPwd] = useState(null);
  const [newPasswordValue, setNewPasswordValue] = useState('');
  const [savingPwd, setSavingPwd] = useState(false);

  const [addBranchOpen, setAddBranchOpen] = useState(false);
  const [newBranch, setNewBranch] = useState({
    branchCode: '',
    branchNameAr: '',
    branchNameEn: '',
    latitude: 30.0444,
    longitude: 31.2357,
    addressText: '',
    isActive: true,
  });
  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });

  const fetchCompanyData = async () => {
    setLoading(true);
    try {
      const [compRes, branchRes, settRes, usersRes] = await Promise.allSettled([
        companyService.getCompany(),
        companyService.getBranches(),
        companyService.getSettings(),
        companyService.getCompanyUsers(),
      ]);

      if (compRes.status === 'fulfilled' && compRes.value) {
        setCompany(compRes.value);
        localStorage.setItem('fastline_company', JSON.stringify(compRes.value));
      } else {
        const saved = localStorage.getItem('fastline_company');
        if (saved) setCompany(JSON.parse(saved));
      }

      if (branchRes.status === 'fulfilled' && branchRes.value) {
        const raw = Array.isArray(branchRes.value) ? branchRes.value : (branchRes.value.items || []);
        const list = raw.map((b) => ({
          ...b,
          id: b.companyBranchId ?? b.id,
          companyBranchId: b.companyBranchId ?? b.id,
        }));
        setBranches(list);
      }

      if (settRes.status === 'fulfilled' && settRes.value) {
        setSettings(settRes.value);
      }

      if (usersRes.status === 'fulfilled' && usersRes.value) {
        const rawU = Array.isArray(usersRes.value) ? usersRes.value : (usersRes.value.items || usersRes.value.data || []);
        setCompanyUsers(rawU.map((u) => ({ ...u, id: u.id || u.userId || u.Id })));
      }
    } catch (e) {
      console.warn('Company fetch error:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCompanyData();
  }, []);

  const handleSaveCompany = async (e) => {
    if (e) e.preventDefault();
    setSavingCompany(true);
    try {
      await companyService.updateCompany(company);
      localStorage.setItem('fastline_company', JSON.stringify(company));
      setToast({ open: true, message: isRtl ? 'تم حفظ بيانات الشركة وتحديثها بنجاح!' : 'Company info updated successfully!', severity: 'success' });
    } catch (e) {
      setToast({ open: true, message: getApiErrorMessage(e, isRtl ? 'تم حفظ التعديلات محلياً' : 'Saved locally'), severity: 'info' });
    } finally {
      setSavingCompany(false);
    }
  };

  const handleSaveSettings = async () => {
    setSavingSettings(true);
    try {
      await companyService.updateSettings(settings);
      setToast({ open: true, message: isRtl ? 'تم تحديث إعدادات النظام بنجاح!' : 'Settings updated successfully!', severity: 'success' });
    } catch (e) {
      setToast({ open: true, message: getApiErrorMessage(e, isRtl ? 'تم تطبيق الإعدادات' : 'Applied settings'), severity: 'info' });
    } finally {
      setSavingSettings(false);
    }
  };

  const handleAddBranch = async (e) => {
    if (e) e.preventDefault();
    if (!newBranch.branchNameAr.trim()) {
      setToast({ open: true, message: isRtl ? 'يرجى إدخال اسم الفرع باللغة العربية' : 'Please enter branch name in Arabic', severity: 'error' });
      return;
    }

    const payload = {
      branchCode: newBranch.branchCode.trim() || `BR-${Date.now().toString().slice(-4)}`,
      branchNameAr: newBranch.branchNameAr.trim(),
      branchNameEn: newBranch.branchNameEn.trim() || newBranch.branchNameAr.trim(),
      latitude: parseFloat(newBranch.latitude) || 0,
      longitude: parseFloat(newBranch.longitude) || 0,
      addressText: (newBranch.addressText || '').trim(),
      isActive: newBranch.isActive,
    };

    setSavingBranch(true);
    try {
      await companyService.createBranch(payload);
      setToast({ open: true, message: `${isRtl ? 'تم إنشاء الفرع بنجاح!' : 'Branch created successfully!'} (${payload.branchNameAr})`, severity: 'success' });
      setAddBranchOpen(false);
      setNewBranch({
        branchCode: '',
        branchNameAr: '',
        branchNameEn: '',
        phone: '',
        addressText: '',
        latitude: 30.0444,
        longitude: 31.2357,
        isActive: true,
      });
      fetchCompanyData();
    } catch (err) {
      setToast({ open: true, message: getApiErrorMessage(err, `Error: ${err.message || ''}`), severity: 'error' });
    } finally {
      setSavingBranch(false);
    }
  };

  const handleAddCompanyUser = async (e) => {
    if (e) e.preventDefault();
    if (!newCompanyUser.userName.trim() || !newCompanyUser.password.trim()) {
      setToast({ open: true, message: isRtl ? 'يرجى إدخال اسم المستخدم وكلمة المرور' : 'Please enter username and password', severity: 'error' });
      return;
    }

    setSavingUser(true);
    try {
      await companyService.createCompanyUser({
        userName: newCompanyUser.userName.trim(),
        fullName: newCompanyUser.fullName.trim() || newCompanyUser.userName.trim(),
        email: newCompanyUser.email.trim(),
        mobile: newCompanyUser.mobile.trim(),
        password: newCompanyUser.password,
        isActive: newCompanyUser.isActive,
        roleIds: [1],
      });
      setToast({ open: true, message: isRtl ? 'تم إضافة مستخدم النظام بنجاح!' : 'User created successfully!', severity: 'success' });
      setAddUserOpen(false);
      setNewCompanyUser({
        userName: '',
        fullName: '',
        email: '',
        mobile: '',
        password: '',
        isActive: true,
        roleIds: [1],
      });
      const uRes = await companyService.getCompanyUsers();
      const rawU = Array.isArray(uRes) ? uRes : (uRes.items || uRes.data || []);
      setCompanyUsers(rawU.map((u) => ({ ...u, id: u.id || u.userId || u.Id })));
    } catch (err) {
      setToast({ open: true, message: getApiErrorMessage(err, isRtl ? 'فشل إضافة المستخدم' : 'Failed to add user'), severity: 'error' });
    } finally {
      setSavingUser(false);
    }
  };

  const handleToggleCompanyUserStatus = async (user) => {
    const uId = user.id || user.userId || user.Id;
    if (!uId) return;
    try {
      const newStatus = !(user.isActive ?? true);
      await companyService.updateCompanyUserStatus(uId, newStatus);
      setToast({ open: true, message: `${isRtl ? 'تم تغيير حالة المستخدم إلى:' : 'User status updated to:'} ${newStatus ? t('active') : t('inactive')}`, severity: 'success' });
      const uRes = await companyService.getCompanyUsers();
      const rawU = Array.isArray(uRes) ? uRes : (uRes.items || uRes.data || []);
      setCompanyUsers(rawU.map((u) => ({ ...u, id: u.id || u.userId || u.Id })));
    } catch (err) {
      setToast({ open: true, message: getApiErrorMessage(err, isRtl ? 'فشل تحديث الحالة' : 'Failed to update status'), severity: 'error' });
    }
  };

  const handleOpenResetPwd = (user) => {
    setSelectedUserForPwd(user);
    setNewPasswordValue('');
    setResetPwdOpen(true);
  };

  const handleSaveResetPwd = async () => {
    const uId = selectedUserForPwd?.id || selectedUserForPwd?.userId || selectedUserForPwd?.Id;
    if (!uId || !newPasswordValue.trim()) {
      setToast({ open: true, message: isRtl ? 'يرجى إدخال كلمة المرور الجديدة' : 'Please enter new password', severity: 'error' });
      return;
    }

    setSavingPwd(true);
    try {
      await companyService.updateCompanyUserPassword(uId, newPasswordValue.trim());
      setToast({ open: true, message: isRtl ? 'تم تحديث كلمة المرور بنجاح!' : 'Password reset successfully!', severity: 'success' });
      setResetPwdOpen(false);
      setSelectedUserForPwd(null);
      setNewPasswordValue('');
    } catch (err) {
      setToast({ open: true, message: getApiErrorMessage(err, isRtl ? 'فشل تحديث كلمة المرور' : 'Failed to reset password'), severity: 'error' });
    } finally {
      setSavingPwd(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <LoadingState />
      </Layout>
    );
  }

  return (
    <Layout>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2.5, flexWrap: 'wrap', gap: 1.5 }}>
        <Box sx={{ textAlign: isRtl ? 'right' : 'left' }}>
          <Typography variant="h5" sx={{ fontWeight: 800, color: 'text.primary', mb: 0.2 }}>
            {t('companyPage.title')}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {t('companyPage.subtitle')}
          </Typography>
        </Box>

        <Button
          variant="contained"
          size="small"
          disabled={savingCompany}
          startIcon={savingCompany ? <CircularProgress size={16} sx={{ color: 'inherit' }} /> : <SaveOutlined sx={{ fontSize: 16 }} />}
          onClick={handleSaveCompany}
          sx={{
            borderRadius: '8px',
            fontSize: '0.8rem',
            py: 0.6,
            background: 'linear-gradient(135deg, #EF6C00 0%, #E65100 100%)',
          }}
        >
          {savingCompany ? (isRtl ? 'جاري الحفظ...' : 'Saving...') : t('companyPage.saveChanges')}
        </Button>
      </Box>

      <Grid container spacing={2.5} dir={dir} style={{ direction: dir }}>
        {/* Left Column: Basic Info & Branding */}
        <Grid item xs={12} md={6}>
          <Card sx={{ mb: 2.5 }}>
            <CardContent sx={{ p: 2.5, textAlign: isRtl ? 'right' : 'left' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                <Avatar sx={{ bgcolor: 'primary.main', width: 44, height: 44 }}>
                  <BusinessOutlined sx={{ fontSize: 24 }} />
                </Avatar>
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                    {isRtl ? (company.companyNameAr || 'فاست لاين للتوصيل') : (company.companyNameEn || 'FastLine Delivery')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {isRtl ? 'كود المنشأة:' : 'Company Code:'} {company.companyCode || 'FASTLINE-01'}
                  </Typography>
                </Box>
              </Box>

              <Divider sx={{ mb: 2 }} />

              <Box component="form" noValidate onSubmit={handleSaveCompany} sx={{ display: 'flex', flexDirection: 'column', gap: 1.8 }}>
                <TextField
                  label={isRtl ? 'اسم الشركة باللغة العربية' : 'Company Name (Arabic)'}
                  fullWidth
                  size="small"
                  value={company.companyNameAr || ''}
                  onChange={(e) => setCompany({ ...company, companyNameAr: e.target.value })}
                />

                <TextField
                  label={isRtl ? 'اسم الشركة باللغة الإنجليزية' : 'Company Name (English)'}
                  fullWidth
                  size="small"
                  value={company.companyNameEn || ''}
                  onChange={(e) => setCompany({ ...company, companyNameEn: e.target.value })}
                />

                <Grid container spacing={1.5} dir={dir} style={{ direction: dir }}>
                  <Grid item xs={6}>
                    <TextField
                      label={isRtl ? 'رقم هاتف الدعم' : 'Support Phone'}
                      fullWidth
                      size="small"
                      value={company.phone || ''}
                      onChange={(e) => setCompany({ ...company, phone: e.target.value })}
                      InputProps={{
                        startAdornment: <PhoneOutlined sx={{ fontSize: 16, color: 'text.secondary' }} />,
                      }}
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <TextField
                      label={isRtl ? 'البريد الإلكتروني' : 'Email'}
                      fullWidth
                      size="small"
                      value={company.email || ''}
                      onChange={(e) => setCompany({ ...company, email: e.target.value })}
                      InputProps={{
                        startAdornment: <EmailOutlined sx={{ fontSize: 16, color: 'text.secondary' }} />,
                      }}
                    />
                  </Grid>
                </Grid>

                <Grid container spacing={1.5} dir={dir} style={{ direction: dir }}>
                  <Grid item xs={6}>
                    <TextField
                      label={isRtl ? 'العملة الافتراضية' : 'Default Currency'}
                      fullWidth
                      size="small"
                      value={company.currencyCode || 'EGP'}
                      onChange={(e) => setCompany({ ...company, currencyCode: e.target.value })}
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <TextField
                      label={isRtl ? 'المنطقة الزمنية' : 'Timezone'}
                      fullWidth
                      size="small"
                      value={company.timeZoneId || 'Africa/Cairo'}
                      onChange={(e) => setCompany({ ...company, timeZoneId: e.target.value })}
                    />
                  </Grid>
                </Grid>

                <TextField
                  label={isRtl ? 'رابط شعار الشركة (Logo URL)' : 'Logo Image URL'}
                  fullWidth
                  size="small"
                  value={company.logoUrl || ''}
                  onChange={(e) => setCompany({ ...company, logoUrl: e.target.value })}
                  placeholder="https://example.com/logo.png"
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Right Column: Dispatch Rules & Branches */}
        <Grid item xs={12} md={6}>
          {/* Dispatch Settings */}
          <Card sx={{ mb: 2.5 }}>
            <CardContent sx={{ p: 2.5, textAlign: isRtl ? 'right' : 'left' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TuneOutlined sx={{ color: 'primary.main', fontSize: 20 }} />
                  <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                    {t('companyPage.dispatchRules')}
                  </Typography>
                </Box>
                <Button size="small" variant="outlined" onClick={handleSaveSettings} sx={{ borderRadius: '6px', fontSize: '0.72rem' }}>
                  {t('save')}
                </Button>
              </Box>

              <Divider sx={{ mb: 2 }} />

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.autoAcceptOrders ?? true}
                      onChange={(e) => setSettings({ ...settings, autoAcceptOrders: e.target.checked })}
                      color="primary"
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        {isRtl ? 'القبول التلقائي للطلبات' : 'Auto Accept Incoming Orders'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {isRtl ? 'استقبال طلبات المطاعم وإدراجها في غرفة العمليات فوراً' : 'Accept restaurant orders into operations queue immediately'}
                      </Typography>
                    </Box>
                  }
                />

                <TextField
                  type="number"
                  label={isRtl ? 'مهلة قبول المندوب للطلب (بالثواني)' : 'Courier Acceptance Timeout (Seconds)'}
                  fullWidth
                  size="small"
                  value={settings.defaultDispatchTimeoutSeconds || 45}
                  onChange={(e) => setSettings({ ...settings, defaultDispatchTimeoutSeconds: parseInt(e.target.value) || 30 })}
                  helperText={isRtl ? 'المهلة الممنوحة للكابتن قبل تحويل الطلب لكابتن آخر' : 'Time before auto re-dispatching to next courier'}
                />

                <TextField
                  type="number"
                  label={isRtl ? 'ساعة بدء اليوم التشغيلي (0-23)' : 'Business Day Start Hour'}
                  fullWidth
                  size="small"
                  value={settings.businessDayStartHour || 6}
                  onChange={(e) => setSettings({ ...settings, businessDayStartHour: parseInt(e.target.value) || 6 })}
                />
              </Box>
            </CardContent>
          </Card>

          {/* Company Branches */}
          <Card>
            <CardContent sx={{ p: 2.5, textAlign: isRtl ? 'right' : 'left' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <StorefrontOutlined sx={{ color: 'primary.main', fontSize: 20 }} />
                  <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                    {t('companyPage.branches')}
                  </Typography>
                </Box>
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<AddCircleOutline sx={{ fontSize: 14 }} />}
                  onClick={() => setAddBranchOpen(true)}
                  sx={{
                    borderRadius: '6px',
                    fontSize: '0.72rem',
                    py: 0.4,
                    background: 'linear-gradient(135deg, #EF6C00 0%, #E65100 100%)',
                    fontWeight: 700,
                  }}
                >
                  {isRtl ? 'إضافة فرع' : 'Add Branch'}
                </Button>
              </Box>

              {branches.length === 0 ? (
                <Box sx={{ p: 2, textAlign: 'center', color: 'text.secondary' }}>
                  <Typography variant="caption">{isRtl ? 'لا توجد فروع مسجلة، أضف فرعك الأول' : 'No branches registered yet'}</Typography>
                </Box>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ textAlign: isRtl ? 'right !important' : 'left !important' }}>{isRtl ? 'كود الفرع' : 'Code'}</TableCell>
                        <TableCell sx={{ textAlign: isRtl ? 'right !important' : 'left !important' }}>{isRtl ? 'اسم الفرع' : 'Branch Name'}</TableCell>
                        <TableCell sx={{ textAlign: isRtl ? 'right !important' : 'left !important' }}>{t('address')}</TableCell>
                        <TableCell sx={{ textAlign: 'center !important' }}>{t('status')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {branches.map((b) => (
                        <TableRow key={b.id} hover>
                          <TableCell sx={{ textAlign: isRtl ? 'right !important' : 'left !important' }}>
                            <Chip label={b.branchCode || `BR-${b.id}`} size="small" sx={{ fontWeight: 700, height: 20, fontSize: '0.68rem' }} />
                          </TableCell>
                          <TableCell sx={{ textAlign: isRtl ? 'right !important' : 'left !important', fontWeight: 700 }}>
                            {b.branchNameAr || b.name}
                          </TableCell>
                          <TableCell sx={{ textAlign: isRtl ? 'right !important' : 'left !important', fontSize: '0.75rem', color: 'text.secondary' }}>
                            {b.addressText || b.address || '-'}
                          </TableCell>
                          <TableCell align="center" sx={{ textAlign: 'center !important' }}>
                            <Chip label={b.isActive !== false ? t('active') : t('inactive')} color={b.isActive !== false ? 'success' : 'default'} size="small" sx={{ height: 20, fontSize: '0.65rem', fontWeight: 700 }} />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Full-width Row: Company Users (إدارة مستخدمي المنظومة) */}
        <Grid item xs={12}>
          <Card>
            <CardContent sx={{ p: 2.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Avatar sx={{ bgcolor: '#8B5CF6', width: 36, height: 36 }}>
                    <GroupOutlined sx={{ fontSize: 20, color: '#fff' }} />
                  </Avatar>
                  <Box sx={{ textAlign: isRtl ? 'right' : 'left' }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                      {isRtl ? 'مستخدمو النظام ولوحة التحكم (Company Users)' : 'Company & Portal Users'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {isRtl ? 'إدارة حسابات مستخدمي لوحة الإدارة، الصلاحيات، وتحديث كلمات المرور' : 'Manage admin users, roles, and reset passwords'}
                    </Typography>
                  </Box>
                </Box>

                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<PersonAddAlt1Outlined sx={{ fontSize: 16 }} />}
                  onClick={() => setAddUserOpen(true)}
                  sx={{ borderRadius: '8px', fontSize: '0.78rem', fontWeight: 700 }}
                >
                  {isRtl ? 'إضافة مستخدم جديد' : 'Add User'}
                </Button>
              </Box>

              <Divider sx={{ mb: 2 }} />

              {companyUsers.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ display: 'block', textAlign: 'center', py: 3 }}>
                  {isRtl ? 'لا يوجد مستخدمون مسجلون حالياً' : 'No company users found'}
                </Typography>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ textAlign: isRtl ? 'right !important' : 'left !important' }}>{isRtl ? 'المستخدم' : 'User'}</TableCell>
                        <TableCell sx={{ textAlign: isRtl ? 'right !important' : 'left !important' }}>{isRtl ? 'اسم الدخول' : 'Username'}</TableCell>
                        <TableCell sx={{ textAlign: isRtl ? 'right !important' : 'left !important' }}>{t('phone')}</TableCell>
                        <TableCell sx={{ textAlign: isRtl ? 'right !important' : 'left !important' }}>{isRtl ? 'البريد' : 'Email'}</TableCell>
                        <TableCell align="center" sx={{ textAlign: 'center !important' }}>{t('status')}</TableCell>
                        <TableCell align="center" sx={{ textAlign: 'center !important' }}>{t('actions')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {companyUsers.map((u, idx) => (
                        <TableRow key={u.id || idx} hover>
                          <TableCell sx={{ textAlign: isRtl ? 'right !important' : 'left !important' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Avatar sx={{ width: 28, height: 28, fontSize: '0.72rem', bgcolor: 'primary.light', color: 'primary.dark', fontWeight: 700 }}>
                                {(u.fullName || u.userName || 'U').charAt(0)}
                              </Avatar>
                              <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.8rem' }}>
                                {u.fullName || u.userName}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell sx={{ textAlign: isRtl ? 'right !important' : 'left !important' }}>
                            <Chip label={`@${u.userName}`} size="small" sx={{ fontWeight: 700, height: 22, fontSize: '0.72rem', bgcolor: 'action.hover' }} />
                          </TableCell>
                          <TableCell sx={{ textAlign: isRtl ? 'right !important' : 'left !important', fontSize: '0.78rem' }}>
                            {u.mobile || u.phone || '-'}
                          </TableCell>
                          <TableCell sx={{ textAlign: isRtl ? 'right !important' : 'left !important', fontSize: '0.78rem' }}>
                            {u.email || '-'}
                          </TableCell>
                          <TableCell align="center" sx={{ textAlign: 'center !important' }}>
                            <Tooltip title={isRtl ? 'انقر لتبديل حالة التفعيل' : 'Click to toggle status'}>
                              <Chip
                                label={u.isActive !== false ? t('active') : t('inactive')}
                                color={u.isActive !== false ? 'success' : 'default'}
                                size="small"
                                onClick={() => handleToggleCompanyUserStatus(u)}
                                sx={{ cursor: 'pointer', fontWeight: 700, height: 22, fontSize: '0.68rem' }}
                              />
                            </Tooltip>
                          </TableCell>
                          <TableCell align="center" sx={{ textAlign: 'center !important' }}>
                            <Tooltip title={isRtl ? 'تعديل / إعادة تعيين كلمة المرور' : 'Reset Password'}>
                              <IconButton size="small" color="warning" onClick={() => handleOpenResetPwd(u)}>
                                <KeyOutlined fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Add Company User Modal */}
      <Dialog
        open={addUserOpen}
        onClose={() => setAddUserOpen(false)}
        maxWidth="sm"
        fullWidth
        dir={dir}
        PaperProps={{ sx: { borderRadius: '14px', p: 1 }, dir: dir, style: { direction: dir } }}
      >
        <DialogTitle sx={{ fontWeight: 800, fontSize: '1rem', pb: 1, textAlign: isRtl ? 'right' : 'left' }}>
          {isRtl ? 'إضافة مستخدم جديد للنظام' : 'Add New Company User'}
        </DialogTitle>
        <DialogContent dir={dir} style={{ direction: dir }}>
          <Box component="form" noValidate dir={dir} style={{ direction: dir }} onSubmit={handleAddCompanyUser} sx={{ display: 'flex', flexDirection: 'column', gap: 1.8, mt: 1 }}>
            <Grid container spacing={1.5} dir={dir} style={{ direction: dir }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label={isRtl ? 'اسم المستخدم (Username) *' : 'Username *'}
                  fullWidth
                  size="small"
                  value={newCompanyUser.userName}
                  onChange={(e) => setNewCompanyUser({ ...newCompanyUser, userName: e.target.value })}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label={isRtl ? 'كلمة المرور *' : 'Password *'}
                  type="password"
                  fullWidth
                  size="small"
                  value={newCompanyUser.password}
                  onChange={(e) => setNewCompanyUser({ ...newCompanyUser, password: e.target.value })}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label={isRtl ? 'الاسم الكامل' : 'Full Name'}
                  fullWidth
                  size="small"
                  value={newCompanyUser.fullName}
                  onChange={(e) => setNewCompanyUser({ ...newCompanyUser, fullName: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label={isRtl ? 'رقم الهاتف' : 'Mobile'}
                  fullWidth
                  size="small"
                  value={newCompanyUser.mobile}
                  onChange={(e) => setNewCompanyUser({ ...newCompanyUser, mobile: e.target.value })}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label={isRtl ? 'البريد الإلكتروني' : 'Email'}
                  fullWidth
                  size="small"
                  value={newCompanyUser.email}
                  onChange={(e) => setNewCompanyUser({ ...newCompanyUser, email: e.target.value })}
                />
              </Grid>
            </Grid>
            <DialogActions sx={{ px: 0, pt: 1, direction: dir }}>
              <Button onClick={() => setAddUserOpen(false)} color="inherit" size="small">
                {t('cancel')}
              </Button>
              <Button
                type="submit"
                variant="contained"
                size="small"
                disabled={savingUser}
                startIcon={savingUser ? <CircularProgress size={16} sx={{ color: 'inherit' }} /> : null}
                sx={{
                  background: 'linear-gradient(135deg, #EF6C00 0%, #E65100 100%)',
                  fontWeight: 700,
                  px: 2.5,
                }}
              >
                {savingUser ? (isRtl ? 'جاري الإضافة...' : 'Adding...') : (isRtl ? 'إضافة المستخدم' : 'Add User')}
              </Button>
            </DialogActions>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Reset Company User Password Dialog */}
      <Dialog
        open={resetPwdOpen}
        onClose={() => setResetPwdOpen(false)}
        maxWidth="xs"
        fullWidth
        dir={dir}
        PaperProps={{ sx: { borderRadius: '14px', p: 1 }, dir: dir, style: { direction: dir } }}
      >
        <DialogTitle sx={{ fontWeight: 800, fontSize: '0.95rem', textAlign: isRtl ? 'right' : 'left' }}>
          {isRtl ? `تعديل كلمة المرور: @${selectedUserForPwd?.userName}` : `Reset Password: @${selectedUserForPwd?.userName}`}
        </DialogTitle>
        <DialogContent dir={dir} style={{ direction: dir }}>
          <Box sx={{ mt: 1 }}>
            <TextField
              label={isRtl ? 'كلمة المرور الجديدة' : 'New Password'}
              type="password"
              fullWidth
              size="small"
              value={newPasswordValue}
              onChange={(e) => setNewPasswordValue(e.target.value)}
              required
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 1.5, direction: dir }}>
          <Button onClick={() => setResetPwdOpen(false)} color="inherit" size="small">
            {t('cancel')}
          </Button>
          <Button
            variant="contained"
            size="small"
            disabled={savingPwd}
            startIcon={savingPwd ? <CircularProgress size={16} sx={{ color: 'inherit' }} /> : null}
            onClick={handleSaveResetPwd}
            sx={{
              background: 'linear-gradient(135deg, #EF6C00 0%, #E65100 100%)',
              fontWeight: 700,
            }}
          >
            {savingPwd ? (isRtl ? 'جاري التحديث...' : 'Updating...') : (isRtl ? 'تحديث كلمة المرور' : 'Update Password')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Branch Modal matching exact Swagger Payload */}
      <Dialog
        open={addBranchOpen}
        onClose={() => setAddBranchOpen(false)}
        maxWidth="sm"
        fullWidth
        dir={dir}
        PaperProps={{ sx: { borderRadius: '14px', p: 1 }, dir: dir, style: { direction: dir } }}
      >
        <DialogTitle sx={{ fontWeight: 800, fontSize: '1rem', pb: 1, textAlign: isRtl ? 'right' : 'left' }}>
          {isRtl ? 'إضافة وتسجيل فرع جديد للمنشأة' : 'Register New Company Branch'}
        </DialogTitle>
        <DialogContent dir={dir} style={{ direction: dir }}>
          <Box component="form" noValidate dir={dir} style={{ direction: dir }} onSubmit={handleAddBranch} sx={{ display: 'flex', flexDirection: 'column', gap: 1.8, mt: 1 }}>
            <Grid container spacing={1.5} dir={dir} style={{ direction: dir }}>
              <Grid item xs={12} sm={4}>
                <TextField
                  label={isRtl ? 'كود الفرع (Branch Code)' : 'Branch Code'}
                  fullWidth
                  size="small"
                  value={newBranch.branchCode}
                  onChange={(e) => setNewBranch({ ...newBranch, branchCode: e.target.value })}
                  placeholder="e.g. BR-CAIRO-01"
                />
              </Grid>

              <Grid item xs={12} sm={8}>
                <TextField
                  label={isRtl ? 'اسم الفرع بالعربية *' : 'Branch Name (Arabic) *'}
                  fullWidth
                  size="small"
                  value={newBranch.branchNameAr}
                  onChange={(e) => setNewBranch({ ...newBranch, branchNameAr: e.target.value })}
                  required
                  placeholder={isRtl ? 'مثال: فرع المعادي / التجمع' : 'e.g. Maadi Branch'}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label={isRtl ? 'اسم الفرع بالإنجليزية' : 'Branch Name (English)'}
                  fullWidth
                  size="small"
                  value={newBranch.branchNameEn}
                  onChange={(e) => setNewBranch({ ...newBranch, branchNameEn: e.target.value })}
                  placeholder="e.g. Maadi Hub"
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label={isRtl ? 'العنوان التفصيلي للفرع' : 'Address Text'}
                  fullWidth
                  size="small"
                  value={newBranch.addressText}
                  onChange={(e) => setNewBranch({ ...newBranch, addressText: e.target.value })}
                  placeholder={isRtl ? 'مثال: شارع النصر، المعادي، القاهرة' : 'e.g. 15 El-Nasr St, Cairo'}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  type="number"
                  label={isRtl ? 'خط العرض (Latitude)' : 'Latitude'}
                  fullWidth
                  size="small"
                  value={newBranch.latitude}
                  onChange={(e) => setNewBranch({ ...newBranch, latitude: e.target.value })}
                  InputProps={{
                    startAdornment: <LocationOnOutlined sx={{ fontSize: 16, color: 'text.secondary' }} />,
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  type="number"
                  label={isRtl ? 'خط الطول (Longitude)' : 'Longitude'}
                  fullWidth
                  size="small"
                  value={newBranch.longitude}
                  onChange={(e) => setNewBranch({ ...newBranch, longitude: e.target.value })}
                  InputProps={{
                    startAdornment: <LocationOnOutlined sx={{ fontSize: 16, color: 'text.secondary' }} />,
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={newBranch.isActive}
                      onChange={(e) => setNewBranch({ ...newBranch, isActive: e.target.checked })}
                      color="success"
                    />
                  }
                  label={
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      {isRtl ? 'الفرع نشط ومتاح للتشغيل وتعيين الكباتن' : 'Branch is active for operations'}
                    </Typography>
                  }
                />
              </Grid>
            </Grid>

            <DialogActions sx={{ px: 0, pt: 1, direction: dir }}>
              <Button onClick={() => setAddBranchOpen(false)} color="inherit" size="small">
                {t('cancel')}
              </Button>
              <Button
                type="submit"
                variant="contained"
                size="small"
                disabled={savingBranch}
                startIcon={savingBranch ? <CircularProgress size={16} sx={{ color: 'inherit' }} /> : null}
                sx={{
                  background: 'linear-gradient(135deg, #EF6C00 0%, #E65100 100%)',
                  fontWeight: 700,
                  px: 2.5,
                }}
              >
                {savingBranch ? (isRtl ? 'جاري الحفظ...' : 'Saving...') : (isRtl ? 'حفظ وإنشاء الفرع' : 'Create Branch')}
              </Button>
            </DialogActions>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Toast */}
      <Snackbar
        open={toast.open}
        autoHideDuration={3000}
        onClose={() => setToast({ ...toast, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: isRtl ? 'left' : 'right' }}
      >
        <Alert severity={toast.severity} sx={{ borderRadius: '8px', fontWeight: 600 }}>
          {toast.message}
        </Alert>
      </Snackbar>
    </Layout>
  );
};

export default CompanyProfile;
